import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../app/store";
import { clearUser } from "../../app/slices/userSlice";
import { clientSsoLogin } from "../../app/api/auth";
import { ReactComponent as ICHNextLogo } from "../../assets/icons/ichNextLogo.svg";
import { AxiosError } from "axios";
import {
  CLIENT_SSO_HOME_PATH,
  CLIENT_SSO_LOGIN_LOCK_KEY,
  clearHandshakeToken,
  consumeClientSsoSessionMessage,
  isClientSsoUser,
  redirectToClientPlatform,
  takeHandshakeToken,
} from "../../constants/clientSso";

const ClientSsoLanding: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.user.userInfo);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("Unable to sign you in");
  const didStart = useRef(false);

  useEffect(() => {
    if (didStart.current || sessionStorage.getItem(CLIENT_SSO_LOGIN_LOCK_KEY)) {
      return;
    }

    const endedMessage = consumeClientSsoSessionMessage();
    if (endedMessage) {
      didStart.current = true;
      dispatch(clearUser());
      setErrorTitle("Session ended");
      setErrorMessage(endedMessage);
      return;
    }

    const token = takeHandshakeToken();

    if (!token && isClientSsoUser(user?.group)) {
      navigate(CLIENT_SSO_HOME_PATH, { replace: true });
      return;
    }

    didStart.current = true;

    if (!token) {
      setErrorMessage(
        "Missing authentication token. Please return to the client platform and try again."
      );
      return;
    }

    sessionStorage.setItem(CLIENT_SSO_LOGIN_LOCK_KEY, "1");
    (async () => {
      try {
        await dispatch(clientSsoLogin(token));
        sessionStorage.removeItem(CLIENT_SSO_LOGIN_LOCK_KEY);
        clearHandshakeToken();
        navigate(CLIENT_SSO_HOME_PATH, { replace: true });
      } catch (error) {
        sessionStorage.removeItem(CLIENT_SSO_LOGIN_LOCK_KEY);
        clearHandshakeToken();
        const apiMessage = (error as AxiosError<{ message?: string }>).response
          ?.data?.message;
        const message =
          apiMessage ||
          "Could not verify your session. Please return to the client platform.";
        setErrorMessage(message);
        toast.error(message, { hideProgressBar: true });
      }
    })();
  }, [dispatch, navigate, user]);

  if (!errorMessage) {
    return (
      <div className="flex">
        <div className="w-64 h-screen bg-gray-100 p-4">
          <Skeleton height="100%" width="100%" className="mb-4" />
        </div>
        <div className="flex-1 flex flex-col">
          <div className="h-24 p-4">
            <Skeleton height="100%" width="100%" />
          </div>
          <div className="h-full p-4">
            <Skeleton height="100%" width="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen font-avenir">
      <div className="p-8 rounded-lg w-96 text-center">
        <ICHNextLogo className="mx-auto mb-12" />
        <h1 className="text-2xl font-semibold text-primary mb-4">
          {errorTitle}
        </h1>
        <p className="text-secondary mb-6">{errorMessage}</p>
        <p className="text-sm text-tertiary mb-6">
          Please go back to the client platform and open Generative again.
        </p>
        <button
          type="button"
          className="font-avenir bg-primary text-inverted h-10 px-6 rounded-[32px]"
          onClick={() => redirectToClientPlatform()}
        >
          Return to client platform
        </button>
      </div>
    </div>
  );
};

export default ClientSsoLanding;
